import React, { useEffect, useState } from "react";
import {
  FilterConfig,
  IDemandPayload,
  IRoot,
} from "../../services/interface/IDemand";
import DemandForecastFilter from "../../components/Filter/demandForecastFilter";
import { useForecastOverViewDropDownFilter } from "../../services/redux/hooks/useDemand";
import {
  SegmentData,
  SegmentDetailPayload,
} from "../../services/interface/IForecastSegmentation";
import { useDispatch, useSelector } from "react-redux";
import { Progress, Table } from "rsuite";
import {
  DataRow,
  FormattedData,
  MonthData,
  PoshVsShippedDrillDownPayload,
  PosvsShippedPayload,
  rawData1,
  rawData2,
  rawData3,
} from "../../services/interface/IPosVsShipped";
import reload from "../../assets/reload.svg";
import moment from "moment";
import {
  useGetPosVsShippedData,
  usePosVsShippedDropDownFilter,
} from "../../services/redux/hooks/usePosVsShipped";
import {
  setApplyFilter,
  setDBFilter,
  setSelectedDBFilter,
  setSelectedFlagMarker,
} from "../../services/redux/reducer/demandfilter.reducer";
import CustomLoader from "../../common/CustomLoader/CustomLoader";

const { ColumnGroup, Column, HeaderCell, Cell } = Table;

const index = () => {
  const dispatch = useDispatch();
  const initialState: IDemandPayload = {
    dc: [],
    itemId: [],
    ucode: [],
    productSegm: [],
    productDesc: [],
    category: [],
    subCategory: [],
    vendorName: [],
    storeNumber: undefined,
    fcwNumber: undefined,
  };

  // Filters - Start
  const filters: FilterConfig[] = [
    { title: "DC", key: "dc", placeholder: "All" },
    { title: "Category", key: "category", placeholder: "All" },
    { title: "SubCategory", key: "subCategory", placeholder: "All" },
    { title: "Segmentation", key: "productSegm", placeholder: "All" },
    { title: "Product Desc.", key: "productDesc", placeholder: "All" },
    { title: "Item Id", key: "itemId", placeholder: "All" },
    { title: "Vendor", key: "vendorName", placeholder: "All" },
  ];

  // selected filter - redux
  const selectedDbFilter = useSelector(
    (state: IRoot) => state.dbfilter.selectedDbFilter
  );
  const isApplied = useSelector((state: IRoot) => state.dbfilter.applyFilter);

  const [filter, setFilter] = useState<IDemandPayload>(initialState);
  const [selectedFilterData, setSelectedFilterData] =
    useState<IDemandPayload>(initialState);
  const [loading, setLoading] = useState<boolean>(false);
  const [detailPayload, setDetailPayload] = useState<PosvsShippedPayload>({
    dc: [],
    itemId: [],
    ucode: [],
    productSegm: [],
    productDesc: [],
    category: [],
    subCategory: [],
    vendor: [],
    accuracy_start: "",
    accuracy_end: "",
    fillRate_start: "",
    fillRate_end: "",
  });
  const [data, setData] = useState<DataRow[]>([]);

  const [dcIdSelected, setDcIdSelected] = useState<string | undefined>();
  const [categoryIdSelected, setCategoryIdSelected] = useState<
    string | undefined
  >();
  const [rangeValue, setRangeValue] = useState<number[]>([0, 100]);
  const [accuracyValue, setAccuracyValue] = useState<number[]>([0, 100]);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);
  const [tableHeight, setTableHeight] = useState<number>(0);

  useEffect(() => {
    if (isApplied === "applied") {
      setDcIdSelected(undefined);
      setCategoryIdSelected(undefined);
      resetData();
      setDetailPayload({
        dc: selectedDbFilter.dc,
        ucode: selectedDbFilter.ucode,
        productSegm: selectedDbFilter.productSegm,
        productDesc: selectedDbFilter.productDesc,
        category: selectedDbFilter.category,
        subCategory: selectedDbFilter.subCategory,
        itemId: selectedDbFilter.itemId ?? [],
        vendor: selectedDbFilter.vendorName,
        accuracy_start: accuracyValue[0].toString(),
        accuracy_end: accuracyValue[1].toString(),
        fillRate_start: rangeValue[0].toString(),
        fillRate_end: rangeValue[1].toString(),
      });
    }
  }, [isApplied]);

  const calculateTableHeight = () => {
    const totalHeight = window.innerHeight;
    const headerHeight = 80;
    const filterBarHeight = filtersExpanded ? 220 : 170;
    const minTableHeight = 350; // Add minimum height

    // Calculate available height for the table
    let availableHeight = totalHeight - (headerHeight + filterBarHeight);

    // Ensure table doesn't get too small
    availableHeight = Math.max(availableHeight, minTableHeight);

    setTableHeight(availableHeight);
  };

  useEffect(() => {
    calculateTableHeight(); // Initial height calculation
    window.addEventListener("resize", calculateTableHeight); // Adjust on resize

    return () => {
      window.removeEventListener("resize", calculateTableHeight); // Cleanup listener
    };
  }, [filtersExpanded]);

  const { mutate: mutateDropDown, data: resFilterData } =
    useForecastOverViewDropDownFilter();

  const {
    mutate: mutateGetTableDetails,
    data: tableDetails,
    isLoading: isTableDetailsLoading,
  } = usePosVsShippedDropDownFilter();

  const {
    mutate: mutateGetDrilldownData,
    data: drilledDownTableData,
    isLoading: isDrilledDownTableDataLoading,
    resetData,
  } = useGetPosVsShippedData();

  const filterHandler = async () => {
    setLoading(true);
    try {
      await mutateDropDown(selectedFilterData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const setFilterData = () => {
    if (resFilterData?.data) {
      setFilter(resFilterData?.data);
      dispatch(setDBFilter(resFilterData?.data));
    }
  };

  const filterSelectionHandler = (key: any, value: any) => {
    setSelectedFilterData({
      ...selectedFilterData,
      [key]: value,
    });
  };

  const removeSelectionHandler = () => {
    setSelectedFilterData(initialState);
    setRangeValue([0, 100]);
    setAccuracyValue([0, 100]);
  };

  useEffect(() => {
    mutateGetTableDetails(detailPayload);
  }, [detailPayload]);

  useEffect(() => {
    if (tableDetails) {
      setData(tableDetails?.data);
    }
  }, [tableDetails]);

  useEffect(() => {
    dispatch(setSelectedDBFilter(selectedFilterData));
  }, [selectedFilterData]);

  useEffect(() => {
    setFilterData();
    return () => {
      dispatch(setSelectedDBFilter(initialState));
    };
  }, [resFilterData?.data]);

  const handleDcIdSelected = () => {
    setCategoryIdSelected(undefined);
    mutateGetDrilldownData({
      dc: dcIdSelected,
      category: null,
    });
  };

  const handlePosVsShipped = () => {
    setDcIdSelected(undefined);
    setCategoryIdSelected(undefined);
    resetData();
    setData(tableDetails?.data);
  };

  // Extract unique months dynamically from the data
  const months = Array.from(
    new Set(data?.flatMap((item) => Object.keys(item.months)))
  );

  // Placeholder for in case of data is missing for a month
  const deafultMonthData: MonthData = {
    orderedQuantity: 0,
    shippedQuantity: 0,
    lostQuantity: 0,
    fillRate: 0,
    posVsShipped: 0,
    actualPos: 0,
    totalForecast: 0,
    accuracy: 0,
  };

  // Transform the data to flatten "months" dynamically
  const transformedData = data?.map((item) => {
    let flattened: FormattedData = {
      DC: item.DC,
      categoryId: item.categoryId || "",
      itemId: item.itemId || "",
    };

    months.forEach((month) => {
      const monthData = item.months[month] || deafultMonthData;

      flattened[`${month}OrderedQuantity`] =
        Math.round(monthData.orderedQuantity) ?? 0;
      flattened[`${month}ShippedQuantity`] =
        Math.round(monthData.shippedQuantity) ?? 0;
      flattened[`${month}LostQuantity`] =
        Math.round(monthData.lostQuantity) ?? 0;
      flattened[`${month}FillRate`] = Math.round(monthData.fillRate) ?? 0;
      flattened[`${month}PosVsShipped`] = monthData?.posVsShipped
        ? Math.round(monthData?.posVsShipped * 100)
        : 0;
      flattened[`${month}ActualPos`] = Math.round(monthData.actualPos) ?? 0;
      flattened[`${month}TotalForecast`] =
        Math.round(monthData.totalForecast) ?? 0;
      flattened[`${month}Accuracy`] = Math.round(monthData.accuracy) ?? 0;
    });

    return flattened;
  });

  const renderProgressBarColor = (value: number) => {
    switch (true) {
      case value < 40:
        return "red";
      case value >= 40 && value < 70:
        return "orange";
      case value >= 70:
        return "green";
      default:
        return "green";
    }
  };

  const handleRowSelect = (rowData: FormattedData) => {
    let payload: PoshVsShippedDrillDownPayload = {
      dc: dcIdSelected ?? null,
      category: categoryIdSelected ?? null,
    };
    if (
      dcIdSelected !== undefined &&
      rowData?.categoryId &&
      typeof rowData?.categoryId === "string" &&
      rowData?.categoryId?.length !== 0
    ) {
      setCategoryIdSelected(rowData?.categoryId);
      payload.category = rowData?.categoryId;
    }
    if (
      dcIdSelected === undefined &&
      rowData?.DC &&
      typeof rowData?.DC === "string" &&
      rowData?.DC?.length !== 0
    ) {
      setDcIdSelected(rowData?.DC);
      payload.dc = rowData?.DC;
    }

    mutateGetDrilldownData(payload);
  };

  useEffect(() => {
    if (drilledDownTableData) {
      setData(drilledDownTableData);
    }
  }, [drilledDownTableData]);

  return (
    <>
      {(isTableDetailsLoading || isDrilledDownTableDataLoading || loading) && (
        <CustomLoader open={true} />
      )}

      <div className="flex flex-col justify-start items-start gap-[2vh] bg-[#f1f1f1] w-full h-[88vh]">
        <div className="relative w-full bg-white">
          <div className="flex justify-between">
            <h3 className="px-[2vw] pt-3 page-title font-semibold">
              {dcIdSelected && drilledDownTableData ? (
                <>
                  <span
                    className="underline underline-offset-2 text-blue-500"
                    onClick={handlePosVsShipped}
                  >
                    <a
                      href=""
                      onClick={($event) => {
                        $event.preventDefault();
                      }}
                    >
                      {"POS vs Shipped"}
                    </a>
                  </span>
                </>
              ) : (
                <>
                  <span>{"POS vs Shipped"}</span>
                </>
              )}

              {dcIdSelected && drilledDownTableData && (
                <>
                  <span>{" > "} </span>
                  {categoryIdSelected ? (
                    <span
                      className="underline underline-offset-2 text-blue-500"
                      onClick={handleDcIdSelected}
                    >
                      <a
                        href=""
                        onClick={($event) => {
                          $event.preventDefault();
                        }}
                      >
                        {dcIdSelected}
                      </a>
                    </span>
                  ) : (
                    <span>{dcIdSelected}</span>
                  )}
                </>
              )}
              {categoryIdSelected && drilledDownTableData && (
                <>
                  <span>{" > "} </span>
                  <span>{categoryIdSelected}</span>
                </>
              )}
            </h3>

            <div className="flex gap-2 items-center pr-[2%]">
              <img src={reload} alt="reload" className="w-[1vw]" />
              <p className="text-fs16 text-[#858585] font-semibold">
                Last Updated :{" "}
                <span>
                  {moment("01-27-2025 11:40 PM"?.slice(0, 10)).format(
                    "M/DD/YYYY"
                  )}
                </span>
              </p>
            </div>
          </div>

          <DemandForecastFilter
            selectedFilterData={selectedFilterData}
            filterSelectionHandler={filterSelectionHandler}
            removeSelectionHandler={removeSelectionHandler}
            resFilterData={filter}
            filters={filters}
            filterHandler={filterHandler}
            alertDesc="Forecast Segmentation"
            biasFilterRqd={false}
            lastUpdated={"05-17-2018 11:40 PM"}
            rangeValue={rangeValue}
            setRangeValue={setRangeValue}
            accuracyValue={accuracyValue}
            setAccuracyValue={setAccuracyValue}
            setFiltersExpanded={setFiltersExpanded}
          />
        </div>

<div className="bg-white w-full flex-grow flex-col">
  <Table
    data={transformedData ? transformedData : []}
    height={tableHeight}
    rowHeight={20}
    wordWrap
    bordered
    cellBordered
    hover
    headerHeight={80}
    className="table-container"
    rowClassName={(rowData, rowIndex) => {
      if (!rowData) return "";
      const key = `${rowData.id}_${rowData.Item}`;
      let className = "rs-table-row";
      if (rowIndex % 2 === 0) {
        className += " db-tablebg";
      }
      if (key === "") {
        className += " rs-row-selection";
      }
      return className;
    }}
    onRowClick={(rowData) => handleRowSelect(rowData)}
  >
    <ColumnGroup 
      align="center" 
      header={<div className="table-header-title font-semibold">Month</div>} 
      fixed
    >
      <Column verticalAlign="center" align="left" width={100} fixed>
        <HeaderCell>
          <div className="table-header-title font-semibold">DC</div>
        </HeaderCell>
        <Cell dataKey="DC">
          {(rowData) =>
            dcIdSelected && drilledDownTableData ? (
              <div className="table-cell-title font-normal">{rowData["DC"]}</div>
            ) : (
              <div className="table-cell-title font-normal">
                <span className="underline underline-offset-2 text-blue-500">
                  <a
                    href=""
                    onClick={($event) => {
                      $event.preventDefault();
                    }}
                  >
                    {rowData["DC"]}
                  </a>
                </span>
              </div>
            )
          }
        </Cell>
      </Column>

      {dcIdSelected && drilledDownTableData && (
        <Column verticalAlign="center" align="left" width={150} fixed>
          <HeaderCell>
            <div className="table-header-title font-semibold">Category</div>
          </HeaderCell>
          <Cell dataKey="categoryId">
            {(rowData) =>
              categoryIdSelected && drilledDownTableData ? (
                <div className="table-cell-title font-normal">{rowData["categoryId"]}</div>
              ) : (
                <div className="table-cell-title font-normal">
                  <span className="underline underline-offset-2 text-blue-500">
                    <a
                      href=""
                      onClick={($event) => {
                        $event.preventDefault();
                      }}
                    >
                      {rowData["categoryId"]}
                    </a>
                  </span>
                </div>
              )
            }
          </Cell>
        </Column>
      )}

      {categoryIdSelected && drilledDownTableData && (
        <Column verticalAlign="center" align="left" width={100} fixed>
          <HeaderCell>
            <div className="table-header-title font-semibold">Item Id</div>
          </HeaderCell>
          <Cell dataKey="itemId">
            {(rowData) => (
              <div className="table-cell-title font-normal">{rowData.itemId}</div>
            )}
          </Cell>
        </Column>
      )}
    </ColumnGroup>

    {months.map((month) => (
      <ColumnGroup
        key={month}
        header={<div className="flex justify-center table-header-title font-semibold">{month}</div>}
      >
        <Column
          verticalAlign="center"
          align="left"
          key={`${month}OrderedQuantity`}
          width={150}
        >
          <HeaderCell>
            <div className="table-header-title font-semibold">Ordered Quantity</div>
          </HeaderCell>
          <Cell dataKey={`${month}OrderedQuantity`}>
            {(rowData) => (
              <div className="table-cell-title font-normal">
                {rowData[`${month}OrderedQuantity`]?.toLocaleString("en-US")}
              </div>
            )}
          </Cell>
        </Column>

        <Column
          verticalAlign="center"
          align="left"
          key={`${month}ShippedQuantity`}
          width={150}
        >
          <HeaderCell>
            <div className="table-header-title font-semibold">Shipped Quantity</div>
          </HeaderCell>
          <Cell dataKey={`${month}ShippedQuantity`}>
            {(rowData) => (
              <div className="table-cell-title font-normal">
                {rowData[`${month}ShippedQuantity`]?.toLocaleString("en-US")}
              </div>
            )}
          </Cell>
        </Column>

        <Column
          verticalAlign="center"
          align="left"
          key={`${month}LostQuantity`}
          width={120}
        >
          <HeaderCell>
            <div className="table-header-title font-semibold">Lost Quantity</div>
          </HeaderCell>
          <Cell dataKey={`${month}LostQuantity`}>
            {(rowData) => (
              <div className="table-cell-title font-normal">
                {rowData[`${month}LostQuantity`]?.toLocaleString("en-US")}
              </div>
            )}
          </Cell>
        </Column>

        <Column key={`${month}FillRate`} width={175}>
          <HeaderCell>
            <div className="table-header-title font-semibold">Fill Rate</div>
          </HeaderCell>
          <Cell>
            {(rowData) => (
              <div className="table-cell-title font-normal">
                <Progress.Line
                  className="pos-vs-shipped-slider"
                  percent={parseFloat(rowData[`${month}FillRate`]?.toFixed(1))}
                  strokeColor={renderProgressBarColor(rowData[`${month}FillRate`])}
                />
              </div>
            )}
          </Cell>
        </Column>

        <Column
          verticalAlign="center"
          align="left"
          key={`${month}PosVsShipped`}
          width={150}
        >
          <HeaderCell>
            <div className="table-header-title font-semibold">POS vs Shipped</div>
          </HeaderCell>
          <Cell dataKey={`${month}PosVsShipped`}>
            {(rowData) => (
              <div className="table-cell-title font-normal">
                {rowData[`${month}PosVsShipped`]}%
              </div>
            )}
          </Cell>
        </Column>

        <Column
          verticalAlign="center"
          align="left"
          key={`${month}ActualPos`}
          width={120}
        >
          <HeaderCell>
            <div className="table-header-title font-semibold">Actual POS</div>
          </HeaderCell>
          <Cell dataKey={`${month}ActualPos`}>
            {(rowData) => (
              <div className="table-cell-title font-normal">
                {rowData[`${month}ActualPos`]?.toLocaleString("en-US")}
              </div>
            )}
          </Cell>
        </Column>

        <Column
          verticalAlign="center"
          align="left"
          key={`${month}TotalForecast`}
          width={120}
        >
          <HeaderCell>
            <div className="table-header-title font-semibold">Total Forecast</div>
          </HeaderCell>
          <Cell dataKey={`${month}TotalForecast`}>
            {(rowData) => (
              <div className="table-cell-title font-normal">
                {rowData[`${month}TotalForecast`]?.toLocaleString("en-US")}
              </div>
            )}
          </Cell>
        </Column>

        <Column key={`${month}Accuracy`} width={175}>
          <HeaderCell>
            <div className="table-header-title font-semibold">Accuracy</div>
          </HeaderCell>
          <Cell>
            {(rowData) => (
              <div className="table-cell-title font-normal">
                <Progress.Line
                  className="pos-vs-shipped-slider"
                  percent={parseFloat(rowData[`${month}Accuracy`]?.toFixed(1))}
                  strokeColor={renderProgressBarColor(rowData[`${month}Accuracy`])}
                />
              </div>
            )}
          </Cell>
        </Column>
      </ColumnGroup>
    ))}
  </Table>

  <div className="py-[0.5vh] px-[1vh] text-fs16 bg-[#f1f1f1]">
    <span className="font-semibold underline underline-offset-2 text-blue-500">
      DC
    </span>{" "}
    and{" "}
    <span className="font-semibold underline underline-offset-2 text-blue-500">
      Category
    </span>{" "}
    can be drilled down
  </div>
</div>
      </div>
    </>
  );
};

export default index;
